import { Currency } from '../models/serializable/currency.js';
import { Price } from '../models/serializable/price.js';
import { GenericOrder } from '../models/serializable/orders/generic-order.js';
import { GenericOrderEntry } from '../models/serializable/orders/generic-order-entry.js';
import { IGenericOrderPaymentHelperAdapter } from './core/generic-order/generic-order-payment-helper-adapter.js';
import { GenericOrderDonationEntry } from '../models/serializable/orders/generic-order-entries/generic-order-donation-entry.js';
import { GenericOrderProductEntry } from '../models/serializable/orders/generic-order-entries/generic-order-product-entry.js';
import { GenericOrderTicketEntry } from '../models/serializable/orders/generic-order-entries/generic-order-ticket-entry.js';
import { GenericOrderTicketSupplementEntry } from '../models/serializable/orders/generic-order-entries/generic-order-ticket-supplement-entry.js';
import { GenericOrderVoucherEntry } from '../models/serializable/orders/generic-order-entries/generic-order-voucher-entry.js';
import { DonationEntryPaymentHelperAdapter } from './core/generic-order/donation-entry-payment-helper-adapter.js';
import { ProductEntryPaymentHelperAdapter } from './core/generic-order/product-entry-payment-helper-adapter.js';
import { TicketEntryPaymentHelperAdapter } from './core/generic-order/ticket-entry-payment-helper-adapter.js';
import { TicketSupplementEntryPaymentHelperAdapter } from './core/generic-order/ticket-supplement-entry-payment-helper-adapter.js';
import { VoucherEntryPaymentHelperAdapter } from './core/generic-order/voucher-entry-payment-helper-adapter.js';
import { GenericOrderDiscountBreakdown } from './core/discount-breakdown.js';
import { GenericOrderFeeBreakdown } from './core/fee-breakdown.js';
import { GenericOrderGiftCardEntry } from '../models/serializable/orders/generic-order-entries/generic-order-gift-card-entry.js';
import { GiftCardEntryPaymentHelperAdapter } from './core/generic-order/gift-card-entry-payment-helper-adapter.js';

export class GenericPaymentHelper {
  private constructor() {
    this._impl = new Map<string, IGenericOrderPaymentHelperAdapter>();

    this._impl.set(GenericOrderDonationEntry.TYPE_ID, new DonationEntryPaymentHelperAdapter());
    this._impl.set(GenericOrderProductEntry.TYPE_ID, new ProductEntryPaymentHelperAdapter());
    this._impl.set(GenericOrderTicketEntry.TYPE_ID, new TicketEntryPaymentHelperAdapter());
    this._impl.set(GenericOrderTicketSupplementEntry.TYPE_ID, new TicketSupplementEntryPaymentHelperAdapter());
    this._impl.set(GenericOrderVoucherEntry.TYPE_ID, new VoucherEntryPaymentHelperAdapter());
    this._impl.set(GenericOrderGiftCardEntry.TYPE_ID, new GiftCardEntryPaymentHelperAdapter());
  }

  private readonly _impl: Map<string, IGenericOrderPaymentHelperAdapter>;

  private static _instance: GenericPaymentHelper = new GenericPaymentHelper();

  static getAdapterForEntry(orderEntry: GenericOrderEntry): IGenericOrderPaymentHelperAdapter | null {
    return this._instance._impl.get(orderEntry.$typeId) ?? null;
  }

  static getCurrencyOfOrder(order: GenericOrder): Currency | null {
    if(order.entries.length <= 0) return null;

    return this.getAdapterForEntry(order.entries[0])?.getCurrency(order, order.entries[0]) ?? null;
  }

  static calculateDiscountBreakdownForOrder(order: GenericOrder): GenericOrderDiscountBreakdown | null {
    const breakdown: GenericOrderDiscountBreakdown = { entries: [] };

    for(let i = 0; i < order.entries.length; i++) {
      const breakdownEntries = this.getAdapterForEntry(order.entries[i])?.calculateDiscountBreakdown(order, order.entries[i], i) ?? null;
      if(breakdownEntries != null) {
        breakdown.entries.push(...breakdownEntries);
      }
    }

    return breakdown;
  }

  static calculateFeeBreakdownForOrder(order: GenericOrder): GenericOrderFeeBreakdown | null {
    const breakdown: GenericOrderFeeBreakdown = { entries: [] };

    for(let i = 0; i < order.entries.length; i++) {
      const breakdownEntries = this.getAdapterForEntry(order.entries[i])?.calculateFeeBreakdown(order, order.entries[i], i) ?? null;
      if(breakdownEntries != null) {
        breakdown.entries.push(...breakdownEntries);
      }
    }

    return breakdown;
  }

  static calculateCheckoutPriceForOrder(order: GenericOrder): Price | null {
    let checkoutPrice: Price | null = null;
    for(const entry of order.entries) {
      const entryPrice = this.getAdapterForEntry(entry)?.calculateCheckoutPrice(order, entry) ?? null;
      if(entryPrice == null) {
        return null;
      }

      checkoutPrice = checkoutPrice != null ? checkoutPrice.add(entryPrice) : entryPrice;
    }

    return checkoutPrice;
  }

  static calculateIncomeForOrder(order: GenericOrder): Price | null {
    let checkoutPrice: Price | null = null;
    for(const entry of order.entries) {
      const entryPrice = this.getAdapterForEntry(entry)?.calculateIncome(order, entry) ?? null;
      if(entryPrice == null) {
        return null;
      }

      checkoutPrice = checkoutPrice != null ? checkoutPrice.add(entryPrice) : entryPrice;
    }

    return checkoutPrice;
  }

  static calculateIncomeWithoutDiscountForOrder(order: GenericOrder): Price | null {
    let checkoutPrice: Price | null = null;
    for(const entry of order.entries) {
      const entryPrice = this.getAdapterForEntry(entry)?.calculateIncomeWithoutDiscount(order, entry) ?? null;

      if(entryPrice == null) {
        return null;
      }

      checkoutPrice = checkoutPrice != null ? checkoutPrice.add(entryPrice) : entryPrice;
    }

    return checkoutPrice;
  }

  static calculateDiscountForOrderEntry(orderEntry: GenericOrderEntry): Price | null {
    return this.getAdapterForEntry(orderEntry)?.calculateDiscount(orderEntry) ?? null;
  }

  static calculateEntryCheckoutPriceForDiscountProvider(orderEntry: GenericOrderEntry): Price | null {
    return this.getAdapterForEntry(orderEntry)?.calculateCheckoutPriceForDiscountProvider(orderEntry) ?? null;
  }

  static canCreateRefundForOrderEntry(order: GenericOrder, orderEntryId: string): boolean | null {
    const orderEntry = order.findEntry(orderEntryId);
    if(orderEntry == null) return null;

    return this.getAdapterForEntry(orderEntry)?.canCreateRefund(order, orderEntry) ?? null;
  }

  static calculateMaxSimulatedRefundForOrderEntry(order: GenericOrder, orderEntryId: string, options: { includeFee: boolean }): Price | null {
    const orderEntry = order.findEntry(orderEntryId);
    if(orderEntry == null) return null;

    return this.getAdapterForEntry(orderEntry)?.calculateMaxSimulatedRefund(order, orderEntry, options) ?? null;
  }
}
